import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  NotificationPoller,
  notificationHelpers,
} from "../../services/notificationApi";
import NotificationItem from "../notifications/NotificationItem";
import { useRealtimeNotifications, useNotificationPermissions } from "../../hooks/useWebSocket";

const NotificationContainer = styled.div`
  position: relative;
`;

const NotificationTrigger = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #3b82f6;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const NotificationPanel = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 380px;
  max-height: 500px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  z-index: 50;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 320px;
    right: -140px;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
`;

const NotificationTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
`;

const MarkAllButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #64748b;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotificationTabs = styled.div`
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
`;

const NotificationTab = styled.button`
  flex: 1;
  padding: 12px 8px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }

  &.active {
    color: #3b82f6;
    border-bottom-color: #3b82f6;
    background: white;
  }
`;

const NotificationList = styled.div`
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`;

// Removed unused styled components - now handled by NotificationItem component

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
`;

const EmptyIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.9rem;
`;

const NotificationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
`;

const FooterButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #64748b;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #475569;
  }
`;

const NotificationCenter = ({ onNotificationClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const panelRef = useRef(null);

  // WebSocket integration
  const {
    trackNotificationRead,
    trackNotificationClicked,
    trackNotificationDismissed,
  } = useRealtimeNotifications({
    onNewNotification: (notification) => {
      // Add new notification to the list
      setNotifications(prev => [notification, ...prev]);
      
      // Update unread count
      fetchUnreadCount();
      
      // Optional: Auto-open notification center for high priority notifications
      if (notification.type === 'ERROR' || notification.priority === 'HIGH') {
        setIsOpen(true);
      }
    },
    onUnreadCountUpdate: (count) => {
      setUnreadCount(count);
    },
    onConnectionChange: (connected) => {
      setIsWebSocketConnected(connected);
      if (connected) {
        // Refresh notifications when reconnected
        fetchNotifications();
        fetchUnreadCount();
      }
    },
  });

  // Browser notification permissions
  const { requestPermission, getPermissionStatus, isSupported } = useNotificationPermissions();

  // Real API data fetching
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getNotifications({
        page: 1,
        limit: 50, // Get more notifications for better UX
        onlyUnread: false,
      });
      
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Keep existing notifications on error
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await getUnreadCount();
      if (response.success) {
        setUnreadCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Set up real-time notification polling
  useEffect(() => {
    const poller = new NotificationPoller(
      (newUnreadCount) => {
        // When unread count changes, refresh notifications
        fetchNotifications();
      },
      30000 // Poll every 30 seconds
    );

    poller.start();

    return () => {
      poller.stop();
    };
  }, [fetchNotifications]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const getFilteredNotifications = () => {
    switch (activeTab) {
      case "unread":
        return notifications.filter((n) => !n.isRead);
      case "alerts":
        return notifications.filter((n) => {
          const priority = notificationHelpers.getPriority(n);
          return priority === "high";
        });
      case "updates":
        return notifications.filter((n) => n.data?.module === "SYSTEM");
      default:
        return notifications;
    }
  };

  // Removed - now handled by NotificationItem component

  const handleNotificationClick = async (notification) => {
    // Track click via WebSocket
    trackNotificationClicked(notification.id, 'click', {
      notificationType: notification.data?.notificationType || notification.type,
      source: 'notification_center',
    });

    // Mark as read if not already read
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification.id);
        
        // Track read via WebSocket
        trackNotificationRead(notification.id, {
          source: 'notification_center_click',
        });

        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)),
        );
        
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    // Handle navigation based on notification data
    if (notification.data?.actionUrl && onNotificationClick) {
      onNotificationClick(notification.data.actionUrl);
    } else if (onNotificationClick) {
      // Default navigation based on module
      const module = notification.data?.module;
      const defaultUrls = {
        ASSESSMENT: '/assessments',
        OKR: '/okrs',
        FEEDBACK: '/feedbacks',
        MEETING: '/meetings',
        SYSTEM: '/dashboard',
      };
      
      if (defaultUrls[module]) {
        onNotificationClick(defaultUrls[module]);
      }
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      
      // Track read via WebSocket
      trackNotificationRead(notificationId, {
        source: 'mark_as_read_button',
      });

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)),
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      
      await markAllNotificationsAsRead();
      
      // Track all reads via WebSocket
      unreadNotifications.forEach(notification => {
        trackNotificationRead(notification.id, {
          source: 'mark_all_as_read',
        });
      });

      setNotifications((prev) => 
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      
      // Update unread count to 0
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      
      // Track dismissal via WebSocket
      trackNotificationDismissed(notificationId, {
        source: 'delete_button',
        action: 'delete',
      });

      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Initialize browser notifications on mount
  useEffect(() => {
    if (isSupported && getPermissionStatus() === 'default') {
      // Auto-request permission for better UX (optional)
      requestPermission();
    }
    
    // Initial data fetch
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount, isSupported, getPermissionStatus, requestPermission]);

  const filteredNotifications = getFilteredNotifications();

  return (
    <NotificationContainer ref={panelRef}>
      <NotificationTrigger
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notificações"
      >
        🔔
        {unreadCount > 0 && (
          <NotificationBadge>{unreadCount}</NotificationBadge>
        )}
      </NotificationTrigger>

      {isOpen && (
        <NotificationPanel>
          <NotificationHeader>
            <NotificationTitle>
              Notificações
              {isWebSocketConnected ? (
                <span style={{ color: '#10b981', fontSize: '0.7rem', marginLeft: '8px' }}>●</span>
              ) : (
                <span style={{ color: '#ef4444', fontSize: '0.7rem', marginLeft: '8px' }}>●</span>
              )}
            </NotificationTitle>
            {unreadCount > 0 && (
              <MarkAllButton onClick={handleMarkAllAsRead}>
                Marcar todas como lidas
              </MarkAllButton>
            )}
          </NotificationHeader>

          <NotificationTabs>
            <NotificationTab
              className={activeTab === "all" ? "active" : ""}
              onClick={() => setActiveTab("all")}
            >
              Todas
            </NotificationTab>
            <NotificationTab
              className={activeTab === "unread" ? "active" : ""}
              onClick={() => setActiveTab("unread")}
            >
              Não lidas ({unreadCount})
            </NotificationTab>
            <NotificationTab
              className={activeTab === "alerts" ? "active" : ""}
              onClick={() => setActiveTab("alerts")}
            >
              Alertas
            </NotificationTab>
            <NotificationTab
              className={activeTab === "updates" ? "active" : ""}
              onClick={() => setActiveTab("updates")}
            >
              Sistema
            </NotificationTab>
          </NotificationTabs>

          <NotificationList>
            {loading ? (
              <EmptyState>
                <EmptyIcon>⏳</EmptyIcon>
                <EmptyText>Carregando notificações...</EmptyText>
              </EmptyState>
            ) : filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onNotificationClick={handleNotificationClick}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDeleteNotification}
                  showActions={true}
                  showModule={true}
                />
              ))
            ) : (
              <EmptyState>
                <EmptyIcon>📭</EmptyIcon>
                <EmptyText>
                  {activeTab === "unread"
                    ? "Nenhuma notificação não lida"
                    : "Nenhuma notificação para exibir"}
                </EmptyText>
              </EmptyState>
            )}
          </NotificationList>

          <NotificationFooter>
            <FooterButton onClick={() => console.log("Configurações")}>
              ⚙️ Configurações
            </FooterButton>
            <FooterButton onClick={() => console.log("Ver todas")}>
              👁️ Ver Todas
            </FooterButton>
          </NotificationFooter>
        </NotificationPanel>
      )}
    </NotificationContainer>
  );
};

export default NotificationCenter;
